import { UserResponse, UserRole } from '@business/entities/account';
import { FileResponse, FileVisibilityType, FolderResponse, MediaItemStatus } from '@business/entities/file';
import { FileService } from '@business/services';
import { Formatter } from '@core/utils/formatter';
import React, { Fragment, memo, useEffect, useMemo, useState } from 'react';
import { Card, CardBody, Col, DropdownMenu, DropdownToggle, UncontrolledDropdown, Badge } from 'reactstrap';
import { HasPermission } from '@core/components/HasPermission';
import { EmptySection } from '@core/components/EmptySection';
interface FileItemProps {
  item: FileResponse | FolderResponse;
  onClick?(): void;
  onRename?(): void;
  onDelete?(): void;
  onMove?(): void;
  onChangeOrg?(): void;
  onShare?(): void;
  onDownload?(): void;
  onVisibility?(): void;
  onShareUsers?(): void;
  onPermission?(): void;
  onStatusChange?(id: string, status: MediaItemStatus): void;
  user: UserResponse | null;
  onDetail?(): void;
  onShareWithClient?(): void;
  users?: UserResponse[];
}

export const FileItem = memo((props: FileItemProps) => {
  const { item, onClick, onShare, onShareWithClient, onVisibility, onShareUsers, user, onChangeOrg, onPermission, users = [] } = props;
  const [isFileShow, setIsFileShow] = useState<boolean>(false);
  const isFile = useMemo(() => item.type === 'file', [item.type]);
  const isPrivate = useMemo(() => item.visibility === FileVisibilityType.private, [item.visibility]);
  const size = useMemo(() => (item.type === 'file' ? (item as FileResponse).size : 0), [item]);
  const files = useMemo(() => (item.type === 'folder' ? (item as FolderResponse).files : 0), [item]);
  const isForm = useMemo(() => item.type === 'file' && item.fileType === 'form', [item]);
  // const isVisible = useMemo(() => user && user.role === 1 && (item.type !== 'folder' || (item.type === 'folder' && item.isParent)), [item, user]);
  const isVisible = useMemo(() => user && user.role === 1, [item, user]);
  const isDocumentOrForm = useMemo(() => item.type === 'file' && (item.fileType === 'document' || item.fileType === 'form'), [item]);
  const isFolder = useMemo(() => item.type === 'folder', [item]);
  const isPresentation = useMemo(() => item.type === 'file' && item.fileType === 'presentation', [item]);
  const isClient = useMemo(
    () => user && !(user.role === UserRole.ClientPortal || user.role === UserRole.subcontractor) && (item as FileResponse).isClient,
    [item, user],
  );
  const isSubcontractor = useMemo(() => {
    const sharedAll =
      user && !(user.role === UserRole.ClientPortal || user.role === UserRole.subcontractor) && (item as FileResponse).isSubcontractor;
    const sharedSpecific = item.users && users.some(u => u.role === UserRole.subcontractor && item.users.includes('user-' + u.id));
    return sharedAll || sharedSpecific;
  }, [item, user]);
  const isPrivateFolder = useMemo(() => {
    if (isFolder) {
      const itemData = item as FolderResponse;
      return !!!itemData.isPublic;
    }
    return false;
  }, [isFolder, item]);

  return (
    <Col xl={4} sm={6}>
      <Card className="shadow-none border hover-pointer" onClick={onClick}>
        <CardBody className="p-3">
          <div className="">
            <div className="float-right ml-2">
              <HasPermission module={'organizations'} moduleKey={'files'} pItems={item.shares}>
                <UncontrolledDropdown className="mb-2">
                  <DropdownToggle
                    tag="a"
                    className="text-muted hover-pointer"
                    onClick={e => {
                      e.stopPropagation();
                    }}
                  >
                    <i className="mdi mdi-dots-horizontal" style={{ fontSize: '20px' }}></i>
                  </DropdownToggle>

                  <DropdownMenu right>
                    {!isFile && (
                      <a
                        className="dropdown-item"
                        onClick={e => {
                          e.stopPropagation();
                          props.onDownload && props.onDownload();
                        }}
                      >
                        Download
                      </a>
                    )}
                    <a
                      className="dropdown-item"
                      onClick={e => {
                        e.stopPropagation();
                        props.onRename && props.onRename();
                      }}
                    >
                      Rename
                    </a>
                    <a
                      className="dropdown-item"
                      onClick={e => {
                        e.stopPropagation();
                        props.onMove && props.onMove();
                      }}
                    >
                      Move
                    </a>
                    <a
                      className="dropdown-item"
                      onClick={e => {
                        e.stopPropagation();
                        props.onChangeOrg && props.onChangeOrg();
                      }}
                    >
                      Change Organization
                    </a>

                    {isForm && (
                      <a
                        className="dropdown-item"
                        onClick={e => {
                          e.stopPropagation();
                          onShare && onShare();
                        }}
                      >
                        Share Publically
                      </a>
                    )}

                    {(item.type == 'file' || item.type == 'folder') && item.fileType != 'system' && (
                      <Fragment>
                        <a
                          className="dropdown-item"
                          onClick={e => {
                            e.stopPropagation();
                            onShareWithClient && onShareWithClient();
                          }}
                        >
                          {item.isClient ? 'Unshare with Client' : 'Share with Client'}
                        </a>
                      </Fragment>
                    )}

                    {isFolder && (
                      <>
                        {isPrivateFolder && (
                          <a
                            className="dropdown-item"
                            onClick={e => {
                              e.stopPropagation();
                              props.onStatusChange && props.onStatusChange(item.id, MediaItemStatus.public);
                            }}
                          >
                            Make Public
                          </a>
                        )}
                        {!isPrivateFolder && (
                          <a
                            className="dropdown-item"
                            onClick={e => {
                              e.stopPropagation();
                              props.onStatusChange && props.onStatusChange(item.id, MediaItemStatus.private);
                            }}
                          >
                            Make Private
                          </a>
                        )}
                      </>
                    )}

                    <a
                      className="dropdown-item"
                      onClick={e => {
                        e.stopPropagation();
                        onPermission && onPermission();
                      }}
                    >
                      Permissions
                    </a>

                    {/* {isVisible && (
                    <a
                      className="dropdown-item"
                      onClick={e => {
                        e.stopPropagation();
                        onVisibility && onVisibility();
                      }}
                    >
                      Make {isPrivate ? 'Public' : 'Private'}
                    </a>
                  )} */}
                    {isVisible && (
                      <a
                        className="dropdown-item"
                        onClick={e => {
                          e.stopPropagation();
                          onShareUsers && onShareUsers();
                        }}
                      >
                        Share Users
                      </a>
                    )}

                    {isPresentation && (
                      <a
                        className="dropdown-item"
                        onClick={e => {
                          e.stopPropagation();
                          props.onDetail && props.onDetail();
                        }}
                      >
                        Summary
                      </a>
                    )}

                    <div className="dropdown-divider"></div>
                    <a
                      className="dropdown-item text-danger"
                      onClick={e => {
                        e.stopPropagation();
                        props.onDelete && props.onDelete();
                      }}
                    >
                      Delete
                    </a>
                  </DropdownMenu>
                </UncontrolledDropdown>
              </HasPermission>
            </div>
            <div className="float-right ml-2">
              {isFolder && !isPrivateFolder && <Badge color="success">Shared Publically</Badge>}
              {isFile && item.fileType == 'system' && <Badge color="secondary">System File</Badge>}
              {item.type === 'file' && item.fileType === 'design' && <Badge color="primary">Design File</Badge>}
              {isClient && (
                <Badge color="info" className="ml-1">
                  Client
                </Badge>
              )}
              {isSubcontractor && (
                <Badge color="warning" className="ml-1">
                  Subcontractor
                </Badge>
              )}
            </div>
            <div className="avatar-xs mr-3 mb-3">
              <div className="avatar-title bg-transparent rounded">
                {isFile && <i className={`${FileService.getIcon(item.name, item as FileResponse)} font-size-24 `}></i>}
                {!isFile && <i className={`bx bxs-folder font-size-24 text-primary`}></i>}
              </div>
            </div>
            <div className="d-flex">
              <div className="overflow-hidden mr-auto">
                <h5 className="font-size-14 text-truncate mb-1">
                  <a className="text-body">{item.name}</a>
                </h5>
              </div>
              {isFile && !isDocumentOrForm && !isPresentation && (
                <div className="align-self-end flex-shrink-0 ml-2">
                  <p className="text-muted mb-0">{FileService.getSize(size)}</p>
                </div>
              )}
              {!isFile && (
                <div className="align-self-end flex-shrink-0 ml-2">
                  <p className="text-muted text-truncate mb-0">
                    {files} {files > 1 ? 'Items' : 'Item'}
                  </p>
                </div>
              )}
            </div>
            <div className="align-self-end flex-shrink-0 mt-1 font-size-10">
              <p className="text-muted mb-0">{Formatter.formatTime(item.created, 'MM/DD/YYYY hh:mm a')}</p>
            </div>
          </div>
        </CardBody>
      </Card>
    </Col>
  );
});
